import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../components/AppIcon';

const Dropdown = ({
  variant = 'singleSelect',
  options = [],
  selected,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  error,
  label,
  className = '',
  width = 'w-full',
  maxHeight = 'max-h-60',
  icon,
  id,
  required = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Generate a unique ID if not provided
  const dropdownId = id || `dropdown-${Math.random().toString(36).substring(2, 9)}`;
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current && (variant === 'singleSelect' || variant === 'multiSelect')) {
      searchInputRef.current.focus();
    }
  }, [isOpen, variant]);
  
  // Handle escape key to close dropdown
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);
  
  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
    }
  };
  
  // Handle option selection
  const handleSelect = (option) => {
    if (variant === 'singleSelect') {
      onChange(option.value);
      setIsOpen(false);
    } else if (variant === 'multiSelect') {
      const newSelected = selected.includes(option.value)
        ? selected.filter(value => value !== option.value)
        : [...selected, option.value];
      onChange(newSelected);
    } else if (variant === 'actionMenu') {
      if (option.onClick) {
        option.onClick();
      }
      setIsOpen(false);
    }
  };
  
  // Filter options based on search term
  const filteredOptions = searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;
  
  // Get display value for single select
  const getDisplayValue = () => {
    if (variant === 'singleSelect') {
      const selectedOption = options.find(option => option.value === selected);
      return selectedOption ? selectedOption.label : placeholder;
    } else if (variant === 'multiSelect') {
      if (!selected || selected.length === 0) return placeholder;
      
      if (selected.length === 1) {
        const selectedOption = options.find(option => option.value === selected[0]);
        return selectedOption ? selectedOption.label : placeholder;
      }
      
      return `${selected.length} items selected`;
    } else if (variant === 'actionMenu' || variant === 'filter') {
      return placeholder;
    }
    
    return placeholder;
  };
  
  // Render dropdown based on variant
  const renderDropdown = () => {
    // Base button classes
    const buttonClasses = `flex items-center justify-between w-full px-3 py-2 text-left bg-white border rounded-md shadow-sm transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed bg-surface' : 'cursor-pointer hover:border-primary'
    } ${
      error ? 'border-error' : 'border-border'
    } ${
      isOpen ? 'border-primary ring-1 ring-primary' : ''
    }`;
    
    // Dropdown menu classes
    const menuClasses = `absolute z-10 w-full mt-1 bg-white border border-border rounded-md shadow-md ${maxHeight} overflow-y-auto`;
    
    switch (variant) {
      case 'singleSelect': case'multiSelect':
        return (
          <div className={`relative ${width} ${className}`} ref={dropdownRef}>
            {label && (
              <label 
                htmlFor={dropdownId} 
                className="block mb-1.5 text-sm font-medium text-text-primary"
              >
                {label}
                {required && <span className="ml-1 text-error">*</span>}
              </label>
            )}
            
            <button
              type="button"
              id={dropdownId}
              className={buttonClasses}
              onClick={toggleDropdown}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              disabled={disabled}
              {...props}
            >
              <span className="truncate">
                {icon && <Icon name={icon} className="inline-block mr-2 text-text-tertiary" size={18} />}
                {getDisplayValue()}
              </span>
              <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} className="text-text-tertiary" size={18} />
            </button>
            
            {isOpen && (
              <div className={menuClasses}>
                <div className="sticky top-0 p-2 bg-white border-b border-border">
                  <div className="relative">
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="w-full px-3 py-2 pl-8 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Icon name="Search" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary" size={16} />
                  </div>
                </div>
                
                <ul className="py-1" role="listbox">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => {
                      const isSelected = variant === 'singleSelect'
                        ? selected === option.value
                        : selected && selected.includes(option.value);
                      
                      return (
                        <li
                          key={option.value}
                          className={`px-3 py-2 cursor-pointer flex items-center ${
                            isSelected
                              ? 'bg-primary bg-opacity-10 text-primary' :'text-text-primary hover:bg-surface'
                          }`}
                          onClick={() => handleSelect(option)}
                          role="option"
                          aria-selected={isSelected}
                        >
                          {variant === 'multiSelect' && (
                            <div className="mr-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                              />
                            </div>
                          )}
                          
                          {option.icon && (
                            <Icon name={option.icon} className="mr-2 text-text-secondary" size={18} />
                          )}
                          
                          <span className="truncate">{option.label}</span>
                          
                          {variant === 'singleSelect' && isSelected && (
                            <Icon name="Check" className="ml-auto text-primary" size={18} />
                          )}
                        </li>
                      );
                    })
                  ) : (
                    <li className="px-3 py-2 text-text-tertiary">No results found</li>
                  )}
                </ul>
              </div>
            )}
            
            {error && (
              <p className="mt-1.5 text-sm text-error">{error}</p>
            )}
          </div>
        );
        
      case 'actionMenu':
        return (
          <div className={`relative ${width} ${className}`} ref={dropdownRef}>
            <button
              type="button"
              className={buttonClasses}
              onClick={toggleDropdown}
              aria-haspopup="menu"
              aria-expanded={isOpen}
              disabled={disabled}
              {...props}
            >
              <span className="truncate">
                {icon && <Icon name={icon} className="inline-block mr-2 text-text-tertiary" size={18} />}
                {placeholder}
              </span>
              <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} className="text-text-tertiary" size={18} />
            </button>
            
            {isOpen && (
              <div className={menuClasses}>
                <ul className="py-1" role="menu">
                  {options.map((option, index) => (
                    <li
                      key={index}
                      className={`px-3 py-2 cursor-pointer flex items-center text-text-primary hover:bg-surface ${
                        option.destructive ? 'text-error' : ''
                      }`}
                      onClick={() => handleSelect(option)}
                      role="menuitem"
                    >
                      {option.icon && (
                        <Icon 
                          name={option.icon} 
                          className={`mr-2 ${option.destructive ? 'text-error' : 'text-text-secondary'}`} 
                          size={18} 
                        />
                      )}
                      <span className="truncate">{option.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
        
      case 'filter':
        return (
          <div className={`relative ${width} ${className}`} ref={dropdownRef}>
            <button
              type="button"
              className={`flex items-center px-3 py-1.5 text-sm border rounded-full ${
                selected && selected.length > 0
                  ? 'bg-primary text-white border-primary' :'bg-white text-text-primary border-border hover:border-primary'
              }`}
              onClick={toggleDropdown}
              aria-haspopup="menu"
              aria-expanded={isOpen}
              disabled={disabled}
              {...props}
            >
              <span className="truncate">
                {icon && <Icon name={icon} className="inline-block mr-1.5" size={16} />}
                {placeholder}
              </span>
              <Icon 
                name={isOpen ? "ChevronUp" : "ChevronDown"} 
                className="ml-1.5" 
                size={16} 
              />
            </button>
            
            {isOpen && (
              <div className={`absolute z-10 right-0 mt-1 bg-white border border-border rounded-md shadow-md ${width} ${maxHeight} overflow-y-auto`}>
                <ul className="py-1" role="menu">
                  {options.map((option, index) => {
                    const isSelected = selected && selected.includes(option.value);
                    
                    return (
                      <li
                        key={index}
                        className={`px-3 py-2 cursor-pointer flex items-center ${
                          isSelected
                            ? 'bg-primary bg-opacity-10 text-primary' :'text-text-primary hover:bg-surface'
                        }`}
                        onClick={() => handleSelect(option)}
                        role="menuitem"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <span className="ml-2 truncate">{option.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return renderDropdown();
};

export default Dropdown;